import React, { useState, useEffect, useCallback, useRef } from 'react';
import './StoryIdeas.css';
import { database, auth, storage } from '../firebaseConfig';
import { ref, push, set, onValue, remove, get, update } from 'firebase/database';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { fetchScreenshotsByIds } from './supabaseConfig';
import { compressGalleryFile } from './galleryImageCompress';

// --- Upload tuning ---
const UPLOAD_CONCURRENCY = 5;        // how many images are uploaded in parallel per batch
// Each upload gets its own filename, so the bytes never change under one URL.
const UPLOAD_CACHE_CONTROL = 'public, max-age=31536000, immutable';

const StoryIdeas = () => {
    const [ideaLists, setIdeaLists] = useState({});
    const [selectedList, setSelectedList] = useState(null);
    const [loading, setLoading] = useState(true);
    const [imageLoading, setImageLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadCount, setUploadCount] = useState({ current: 0, total: 0 });
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [newListName, setNewListName] = useState('');

    // Scene state
    const [activeScene, setActiveScene] = useState(null);
    const [sceneImages, setSceneImages] = useState([]);
    const [showCreateSceneModal, setShowCreateSceneModal] = useState(false);
    const [newSceneName, setNewSceneName] = useState('');
    const [newSceneDescription, setNewSceneDescription] = useState('');
    const [editingScene, setEditingScene] = useState(null);
    const [editSceneNameValue, setEditSceneNameValue] = useState('');
    const [editSceneTextValue, setEditSceneTextValue] = useState('');

    // Notes state
    const [newNoteText, setNewNoteText] = useState('');
    const [newNoteLabel, setNewNoteLabel] = useState('');
    const [editingNote, setEditingNote] = useState(null);
    const [editNoteText, setEditNoteText] = useState('');

    const selectedListRef = useRef(selectedList);
    useEffect(() => { selectedListRef.current = selectedList; }, [selectedList]);

    const NOTE_LABELS = [
        { id: 'mood', label: 'Mood', color: '#8b5cf6' },
        { id: 'direction', label: 'Direction', color: '#3b82f6' },
        { id: 'props', label: 'Props', color: '#f59e0b' },
        { id: 'lighting', label: 'Lighting', color: '#eab308' },
        { id: 'color', label: 'Color', color: '#ec4899' },
        { id: 'sound', label: 'Sound', color: '#14b8a6' },
        { id: 'copy', label: 'Copy', color: '#6366f1' },
        { id: 'reference', label: 'Reference', color: '#f97316' },
        { id: 'note', label: 'Note', color: '#64748b' },
    ];

    // ---- DATA LOADING ----
    useEffect(() => {
        if (!auth.currentUser) { setLoading(false); return; }

        const listsRef = ref(database, `stories/${auth.currentUser.uid}/ideas`);
        const timeout = setTimeout(() => { setLoading(false); setIdeaLists({}); }, 3000);

        const unsubscribe = onValue(listsRef, (snapshot) => {
            clearTimeout(timeout);
            setLoading(false);
            if (snapshot.exists()) {
                const data = snapshot.val();
                setIdeaLists(data);
                if (!selectedListRef.current && Object.keys(data).length > 0) {
                    setSelectedList(Object.keys(data)[0]);
                }
            } else {
                setIdeaLists({});
                setSelectedList(null);
            }
        }, () => { clearTimeout(timeout); setLoading(false); setIdeaLists({}); });

        return () => { clearTimeout(timeout); unsubscribe(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Reset note/image state when switching boards
    useEffect(() => {
        setNewNoteText('');
        setNewNoteLabel('');
        setEditingNote(null);
        setEditNoteText('');
        setSceneImages([]);
        setActiveScene(null);
    }, [selectedList]);

    // Auto-select first scene only if none is active or current was deleted
    useEffect(() => {
        if (!selectedList || !ideaLists[selectedList]) {
            if (activeScene) setActiveScene(null);
            return;
        }
        const scenes = ideaLists[selectedList].scenes || {};
        const keys = Object.keys(scenes);
        if (!activeScene || !scenes[activeScene]) {
            setActiveScene(keys.length > 0 ? keys[0] : null);
        }
    }, [selectedList, ideaLists, activeScene]);

    // Image loader
    const loadImagesFromIds = useCallback(async (supabaseIds, uploadedIds) => {
        const ids = supabaseIds || [];
        const uploaded = uploadedIds || [];
        if (ids.length === 0 && uploaded.length === 0) return [];

        let all = [];

        if (uploaded.length > 0) {
            try {
                const promises = uploaded.map(async (imageId) => {
                    const snap = await get(ref(database, `stories/${auth.currentUser.uid}/uploaded-images/${imageId}`));
                    if (snap.exists()) return { ...snap.val(), source: 'firebase', supabase_storage_url: snap.val().firebase_storage_url };
                    return null;
                });
                all.push(...(await Promise.all(promises)).filter(Boolean));
            } catch (e) { console.error('Error loading uploaded images:', e); }
        }

        if (ids.length > 0) {
            try {
                const data = await fetchScreenshotsByIds(ids, [
                    'id', 'filename', 'supabase_storage_url', 'quality_score',
                    'emotion_score', 'composition_score', 'scene_type', 'content_type', 'description'
                ]);
                all.push(...ids.map(id => data.find(img => img.id === id)).filter(Boolean).map(img => ({ ...img, source: 'supabase' })));
            } catch (e) { console.error('Error loading API images:', e); }
        }
        return all;
    }, []);

    // Load active scene images
    const loadSceneImages = useCallback(async () => {
        if (!activeScene || !selectedList) return;
        const scene = ideaLists[selectedList]?.scenes?.[activeScene];
        if (!scene) { setSceneImages([]); return; }
        setImageLoading(true);
        try {
            setSceneImages(await loadImagesFromIds(scene.imageIds, scene.uploadedImages));
        } catch (e) { console.error('Error loading scene images:', e); }
        finally { setImageLoading(false); }
    }, [activeScene, selectedList, ideaLists, loadImagesFromIds]);

    useEffect(() => { loadSceneImages(); }, [loadSceneImages]);

    // ---- BOARD CRUD ----
    const createNewList = async () => {
        if (!newListName.trim() || !auth.currentUser) return;
        const userId = auth.currentUser.uid;
        const boardRef = push(ref(database, `stories/${userId}/ideas`));
        const firstSceneRef = push(ref(database, `stories/${userId}/ideas/${boardRef.key}/scenes`));
        try {
            setActiveScene(null);
            setSceneImages([]);
            setNewNoteText('');
            setNewNoteLabel('');
            setEditingNote(null);
            setEditNoteText('');

            await set(boardRef, {
                name: newListName.trim(),
                createdAt: Date.now(),
                scenes: {
                    [firstSceneRef.key]: {
                        name: 'Scene 1',
                        description: '',
                        createdAt: Date.now(),
                        imageIds: [],
                        uploadedImages: []
                    }
                }
            });
            setNewListName('');
            setShowCreateModal(false);
            setSelectedList(boardRef.key);
        } catch (e) { console.error('Error creating board:', e); }
    };

    const deleteList = async (listId) => {
        if (!auth.currentUser || !window.confirm('Delete this board and all its scenes?')) return;
        try {
            const shareToken = ideaLists[listId]?.shareToken;
            await remove(ref(database, `stories/${auth.currentUser.uid}/ideas/${listId}`));
            if (shareToken) await remove(ref(database, `shared-lists/${shareToken}`));
            if (selectedList === listId) { setSelectedList(null); setActiveScene(null); }
        } catch (e) { console.error('Error deleting board:', e); }
    };

    // ---- SCENE CRUD ----
    const createScene = async () => {
        if (!newSceneName.trim() || !auth.currentUser || !selectedList) return;
        const scenesRef = ref(database, `stories/${auth.currentUser.uid}/ideas/${selectedList}/scenes`);
        const newRef = push(scenesRef);
        try {
            await set(newRef, {
                name: newSceneName.trim(),
                description: newSceneDescription.trim(),
                createdAt: Date.now(),
                imageIds: [],
                uploadedImages: []
            });
            setNewSceneName('');
            setNewSceneDescription('');
            setShowCreateSceneModal(false);
            setActiveScene(newRef.key);
        } catch (e) { console.error('Error creating scene:', e); }
    };

    const deleteScene = async (sceneId) => {
        if (!auth.currentUser || !selectedList) return;
        const scenes = ideaLists[selectedList]?.scenes || {};
        if (Object.keys(scenes).length <= 1) {
            alert('A board must have at least one scene.');
            return;
        }
        if (!window.confirm('Delete this scene and all its images?')) return;
        try {
            await remove(ref(database, `stories/${auth.currentUser.uid}/ideas/${selectedList}/scenes/${sceneId}`));
            if (activeScene === sceneId) setActiveScene(null);
        } catch (e) { console.error('Error deleting scene:', e); }
    };

    const saveSceneEdit = async () => {
        if (!editingScene || !auth.currentUser || !selectedList) return;
        try {
            await update(ref(database, `stories/${auth.currentUser.uid}/ideas/${selectedList}/scenes/${editingScene}`), {
                name: editSceneNameValue.trim(),
                description: editSceneTextValue.trim()
            });
            setEditingScene(null);
        } catch (e) { console.error('Error updating scene:', e); }
    };

    // ---- NOTES CRUD ----
    const addNote = async () => {
        if (!newNoteText.trim() || !auth.currentUser || !selectedList || !activeScene) return;
        const notesRef = ref(database, `stories/${auth.currentUser.uid}/ideas/${selectedList}/scenes/${activeScene}/notes`);
        const newRef = push(notesRef);
        try {
            await set(newRef, {
                text: newNoteText.trim(),
                label: newNoteLabel || 'note',
                createdAt: Date.now(),
            });
            setNewNoteText('');
            setNewNoteLabel('');
        } catch (e) { console.error('Error adding note:', e); }
    };

    const deleteNote = async (noteId) => {
        if (!auth.currentUser || !selectedList || !activeScene) return;
        try {
            await remove(ref(database, `stories/${auth.currentUser.uid}/ideas/${selectedList}/scenes/${activeScene}/notes/${noteId}`));
        } catch (e) { console.error('Error deleting note:', e); }
    };

    const saveNoteEdit = async (noteId) => {
        if (!editNoteText.trim() || !auth.currentUser || !selectedList || !activeScene) return;
        try {
            await update(ref(database, `stories/${auth.currentUser.uid}/ideas/${selectedList}/scenes/${activeScene}/notes/${noteId}`), {
                text: editNoteText.trim()
            });
            setEditingNote(null);
            setEditNoteText('');
        } catch (e) { console.error('Error updating note:', e); }
    };

    const getLabelConfig = (labelId) => NOTE_LABELS.find(l => l.id === labelId) || NOTE_LABELS[NOTE_LABELS.length - 1];

    const formatNoteDate = (ts) => {
        const d = new Date(ts);
        const now = new Date();
        const diff = now - d;
        if (diff < 60000) return 'Just now';
        if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
        if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
        if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined });
    };

    // ---- SHARE ----
    const togglePublicShare = async (listId, isPublic) => {
        if (!auth.currentUser) return;
        const userId = auth.currentUser.uid;
        const listRef = ref(database, `stories/${userId}/ideas/${listId}`);
        try {
            if (isPublic) {
                const shareToken = `${userId}_${listId}_${Date.now()}`;
                await update(listRef, { isPublic: true, shareToken, sharedAt: Date.now() });
                await set(ref(database, `shared-lists/${shareToken}`), { userId, listId, isLive: true, createdAt: Date.now(), createdBy: auth.currentUser.email || 'Unknown' });
                await navigator.clipboard.writeText(`${window.location.origin}/shared/${shareToken}`);
                alert('Board is now public! Link copied.');
            } else {
                const shareToken = ideaLists[listId]?.shareToken;
                await update(listRef, { isPublic: false, shareToken: null, sharedAt: null });
                if (shareToken) await remove(ref(database, `shared-lists/${shareToken}`));
                alert('Board is now private.');
            }
        } catch (e) { console.error('Error toggling share:', e); }
    };

    const copyShareLink = async (listId) => {
        const t = ideaLists[listId]?.shareToken;
        if (t) { await navigator.clipboard.writeText(`${window.location.origin}/shared/${t}`); alert('Link copied!'); }
    };

    // ---- IMAGE OPERATIONS (always target active scene) ----
    const getSceneRef = () => ref(database, `stories/${auth.currentUser.uid}/ideas/${selectedList}/scenes/${activeScene}`);
    const getSceneData = () => ideaLists[selectedList]?.scenes?.[activeScene];

    const removeImageFromList = async (imageId) => {
        if (!selectedList || !activeScene || !auth.currentUser) return;
        const scene = getSceneData();
        if (!scene) return;
        try {
            await update(getSceneRef(), {
                imageIds: (scene.imageIds || []).filter(id => id !== imageId),
                uploadedImages: (scene.uploadedImages || []).filter(id => id !== imageId)
            });
        } catch (e) { console.error('Error removing image:', e); }
    };

    // Compress + upload a single file, then return its DB record (no DB write yet).
    const uploadSingleImage = async (file, userId) => {
        const { blob, type, name } = await compressGalleryFile(file);
        const timestamp = Date.now();
        const randomId = Math.random().toString(36).substr(2, 9);
        const imageRef = storageRef(storage, `image-uploads/${timestamp}_${randomId}-${name}`);
        await uploadBytes(imageRef, blob, { contentType: type, cacheControl: UPLOAD_CACHE_CONTROL });
        const downloadURL = await getDownloadURL(imageRef);

        return {
            id: `uploaded_${timestamp}_${randomId}`,
            filename: name,
            firebase_storage_url: downloadURL,
            uploadedAt: timestamp,
            uploadedBy: userId,
            source: 'firebase',
            quality_score: 5.0,
            scene_type: 'uploaded',
            content_type: 'user_upload',
            description: `Uploaded: ${name}`,
            fileSize: blob.size,
            fileType: type
        };
    };

    const handleImageUpload = async (files) => {
        if (!selectedList || !activeScene || !auth.currentUser || !files || files.length === 0) return;

        const fileList = Array.from(files);
        setUploading(true);
        setShowUploadModal(true);
        setUploadProgress(0);
        setUploadCount({ current: 0, total: fileList.length });

        const userId = auth.currentUser.uid;
        const scene = getSceneData();
        const sceneRef = getSceneRef();
        const baseImageIds = scene?.uploadedImages || [];

        let completed = 0;
        let failed = 0;
        const addedImageIds = [];

        try {
            // Process the files in batches with limited parallelism so we never
            // push a huge payload at once and a single bad image can't abort the run.
            for (let start = 0; start < fileList.length; start += UPLOAD_CONCURRENCY) {
                const batch = fileList.slice(start, start + UPLOAD_CONCURRENCY);
                const results = await Promise.allSettled(
                    batch.map((file) => uploadSingleImage(file, userId))
                );

                const batchImages = [];
                results.forEach((r) => {
                    completed++;
                    if (r.status === 'fulfilled') {
                        batchImages.push(r.value);
                    } else {
                        failed++;
                        console.error('Image upload failed:', r.reason);
                    }
                    setUploadCount({ current: completed, total: fileList.length });
                    setUploadProgress((completed / fileList.length) * 100);
                });

                if (batchImages.length > 0) {
                    // One multi-path write for all image nodes of this batch.
                    const updates = {};
                    batchImages.forEach((img) => {
                        updates[`stories/${userId}/uploaded-images/${img.id}`] = img;
                    });
                    await update(ref(database), updates);

                    // Persist progress to the scene after every batch so partial
                    // uploads survive even if a later batch fails.
                    addedImageIds.push(...batchImages.map((i) => i.id));
                    await update(sceneRef, {
                        uploadedImages: [...baseImageIds, ...addedImageIds]
                    });
                }
            }

            setUploadProgress(100);
            if (failed > 0) {
                alert(`${failed} of ${fileList.length} image(s) could not be uploaded. The rest were added successfully.`);
            }
            setTimeout(() => setShowUploadModal(false), 1500);
        } catch (e) {
            console.error('Error uploading:', e);
            alert('Upload failed. Please try again.');
            setShowUploadModal(false);
        } finally {
            setUploading(false);
            const input = document.getElementById('image-upload');
            if (input) input.value = '';
        }
    };

    const downloadSceneImages = async () => {
        if (sceneImages.length === 0) { alert('No images to download.'); return; }
        const scene = getSceneData();
        const label = scene?.name || 'scene';
        try {
            const JSZip = (await import('jszip')).default;
            const zip = new JSZip();
            const folderName = `${label.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_images`;
            const folder = zip.folder(folderName);
            let count = 0;
            for (let i = 0; i < sceneImages.length; i++) {
                const url = sceneImages[i]?.url || sceneImages[i]?.supabase_storage_url;
                if (!url) continue;
                try {
                    const res = await fetch(url, { method: 'GET', mode: 'cors', credentials: 'omit' });
                    if (!res.ok) continue;
                    const blob = await res.blob();
                    folder.file(`image_${i + 1}_${sceneImages[i].id}.${blob.type.split('/')[1] || 'jpg'}`, blob);
                    count++;
                } catch (e) { /* skip */ }
            }
            if (count === 0) { alert('No images could be downloaded.'); return; }
            const content = await zip.generateAsync({ type: 'blob' });
            const a = document.createElement('a');
            a.href = URL.createObjectURL(content);
            a.download = `${folderName}.zip`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            alert(`Downloaded ${count} images!`);
        } catch (e) { alert('Download failed.'); }
    };

    // ---- HELPERS ----
    const boardImageCount = (boardId) => {
        const scenes = ideaLists[boardId]?.scenes || {};
        return Object.values(scenes).reduce((sum, s) => sum + (s.imageIds || []).length + (s.uploadedImages || []).length, 0);
    };

    const sceneImageCount = (scene) => ((scene?.imageIds || []).length) + ((scene?.uploadedImages || []).length);

    // ---- RENDER ----
    if (loading) {
        return <div className="si-loading" style={{ minHeight: '100vh' }}><div className="si-spinner" /><p>Loading your boards...</p></div>;
    }

    const listKeys = Object.keys(ideaLists);
    const selectedListData = selectedList ? ideaLists[selectedList] : null;
    const scenes = selectedListData?.scenes || {};
    const sceneKeys = Object.keys(scenes);
    const activeSceneData = activeScene ? scenes[activeScene] : null;

    return (
        <div className="si-layout">
            {/* SIDEBAR */}
            <aside className="si-sidebar">
                <div className="si-sidebar-top">
                    <p className="si-sidebar-label">Your Boards</p>
                    <button className="si-new-board-btn" onClick={() => setShowCreateModal(true)}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
                        New Board
                    </button>
                </div>
                <nav className="si-board-list">
                    {listKeys.length === 0 && <div className="si-board-empty"><p>No boards yet</p></div>}
                    {listKeys.map(listId => (
                        <div key={listId} className={`si-board-item ${selectedList === listId ? 'active' : ''}`} onClick={() => setSelectedList(listId)}>
                            <div className="si-board-item-icon">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/>
                                </svg>
                            </div>
                            <div className="si-board-item-info">
                                <span className="si-board-item-name">{ideaLists[listId].name}</span>
                                <span className="si-board-item-count">{boardImageCount(listId)} images · {Object.keys(ideaLists[listId].scenes || {}).length} scenes</span>
                            </div>
                            <button className="si-board-item-delete" onClick={(e) => { e.stopPropagation(); deleteList(listId); }} title="Delete board">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14"/>
                                </svg>
                            </button>
                        </div>
                    ))}
                </nav>
            </aside>

            {/* MAIN */}
            <main className="si-main">
                {selectedListData ? (
                    <>
                        {/* BOARD HEADER */}
                        <div className="si-content-header">
                            <div className="si-content-title">
                                <h2>{selectedListData.name}</h2>
                                <div className="si-share-toggle">
                                    <label className="si-toggle">
                                        <input type="checkbox" checked={selectedListData?.isPublic || false} onChange={(e) => togglePublicShare(selectedList, e.target.checked)} disabled={boardImageCount(selectedList) === 0} />
                                        <span className="si-toggle-track"><span className="si-toggle-thumb" /></span>
                                        <span className="si-toggle-text">Public</span>
                                    </label>
                                    {selectedListData?.isPublic && (
                                        <button className="si-icon-btn si-icon-btn-sm" onClick={() => copyShareLink(selectedList)} title="Copy link">
                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/>
                                            </svg>
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* SCENE FOLDERS */}
                        <div className="si-scenes-section">
                            <div className="si-scenes-header">
                                <p className="si-sidebar-label">Scenes</p>
                            </div>
                            <div className="si-scenes-grid">
                                {sceneKeys.map(sceneId => {
                                    const scene = scenes[sceneId];
                                    const isActive = activeScene === sceneId;
                                    return (
                                        <div key={sceneId} className={`si-scene-folder ${isActive ? 'active' : ''}`} onClick={() => setActiveScene(sceneId)}>
                                            <div className="si-scene-folder-top">
                                                <div className="si-scene-folder-icon">
                                                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                                        <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/>
                                                    </svg>
                                                </div>
                                                <div className="si-scene-folder-actions">
                                                    <button className="si-scene-edit-btn" onClick={(e) => {
                                                        e.stopPropagation();
                                                        setEditingScene(sceneId);
                                                        setEditSceneNameValue(scene.name);
                                                        setEditSceneTextValue(scene.description || '');
                                                    }} title="Edit">
                                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                            <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
                                                        </svg>
                                                    </button>
                                                    <button className="si-scene-delete-btn" onClick={(e) => { e.stopPropagation(); deleteScene(sceneId); }} title="Delete">
                                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                            <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14"/>
                                                        </svg>
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="si-scene-folder-info">
                                                <span className="si-scene-folder-name">{scene.name}</span>
                                                {scene.description && <p className="si-scene-folder-desc">{scene.description}</p>}
                                                <span className="si-scene-folder-count">{sceneImageCount(scene)} images</span>
                                            </div>
                                        </div>
                                    );
                                })}

                                <div className="si-scene-folder si-scene-folder-add" onClick={() => setShowCreateSceneModal(true)}>
                                    <div className="si-scene-add-inner">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
                                        <span>New Scene</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* ACTIVE SCENE CONTENT */}
                        {activeSceneData && (() => {
                            const notes = activeSceneData.notes || {};
                            const noteKeys = Object.keys(notes).sort((a, b) => (notes[b].createdAt || 0) - (notes[a].createdAt || 0));

                            return (
                            <div className="si-scene-content">
                                <div className="si-scene-content-header">
                                    <div className="si-scene-content-title">
                                        <h3>{activeSceneData.name}</h3>
                                        <span className="si-content-count">{sceneImages.length} images</span>
                                    </div>
                                    <div className="si-content-actions">
                                        <input type="file" id="image-upload" accept="image/*" multiple onChange={(e) => handleImageUpload(e.target.files)} style={{ display: 'none' }} />
                                        <button className="si-action-btn si-upload-btn" onClick={() => document.getElementById('image-upload').click()} disabled={uploading}>
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12"/></svg>
                                            Upload
                                        </button>
                                        <button className="si-icon-btn" onClick={downloadSceneImages} title="Download as ZIP" disabled={sceneImages.length === 0}>
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/></svg>
                                        </button>
                                    </div>
                                </div>

                                {activeSceneData.description && (
                                    <div className="si-scene-description">
                                        <p>{activeSceneData.description}</p>
                                    </div>
                                )}

                                {/* NOTES SECTION */}
                                <div className="si-notes-section">
                                    <div className="si-notes-header">
                                        <p className="si-sidebar-label">Notes</p>
                                        <span className="si-notes-count">{noteKeys.length}</span>
                                    </div>

                                    {/* Quick-add note */}
                                    <div className="si-note-composer">
                                        <div className="si-note-composer-labels">
                                            {NOTE_LABELS.map(l => (
                                                <button
                                                    key={l.id}
                                                    className={`si-note-label-pill ${newNoteLabel === l.id ? 'active' : ''}`}
                                                    style={{ '--label-color': l.color }}
                                                    onClick={() => setNewNoteLabel(newNoteLabel === l.id ? '' : l.id)}
                                                >
                                                    {l.label}
                                                </button>
                                            ))}
                                        </div>
                                        <div className="si-note-composer-input">
                                            <textarea
                                                className="si-note-textarea"
                                                placeholder="Add a note — describe the mood, direction, props, references..."
                                                value={newNoteText}
                                                onChange={(e) => setNewNoteText(e.target.value)}
                                                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); addNote(); } }}
                                                rows={2}
                                            />
                                            <button className="si-note-add-btn" onClick={addNote} disabled={!newNoteText.trim()}>
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
                                            </button>
                                        </div>
                                    </div>

                                    {/* Notes timeline */}
                                    {noteKeys.length > 0 && (
                                        <div className="si-notes-timeline">
                                            {noteKeys.map(noteId => {
                                                const note = notes[noteId];
                                                const labelCfg = getLabelConfig(note.label);
                                                const isEditing = editingNote === noteId;

                                                return (
                                                    <div key={noteId} className="si-note-card" style={{ '--note-accent': labelCfg.color }}>
                                                        <div className="si-note-card-dot" />
                                                        <div className="si-note-card-body">
                                                            <div className="si-note-card-top">
                                                                <span className="si-note-card-label" style={{ background: labelCfg.color }}>{labelCfg.label}</span>
                                                                <span className="si-note-card-time">{formatNoteDate(note.createdAt)}</span>
                                                                <div className="si-note-card-actions">
                                                                    <button onClick={() => {
                                                                        if (isEditing) { setEditingNote(null); } else { setEditingNote(noteId); setEditNoteText(note.text); }
                                                                    }} title={isEditing ? 'Cancel' : 'Edit'}>
                                                                        {isEditing ? (
                                                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
                                                                        ) : (
                                                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                                                        )}
                                                                    </button>
                                                                    <button onClick={() => deleteNote(noteId)} title="Delete">
                                                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                            <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14"/>
                                                                        </svg>
                                                                    </button>
                                                                </div>
                                                            </div>
                                                            {isEditing ? (
                                                                <div className="si-note-edit-wrap">
                                                                    <textarea className="si-note-edit-textarea" value={editNoteText} onChange={(e) => setEditNoteText(e.target.value)} rows={3} autoFocus />
                                                                    <button className="si-btn-primary si-btn-xs" onClick={() => saveNoteEdit(noteId)} disabled={!editNoteText.trim()}>Save</button>
                                                                </div>
                                                            ) : (
                                                                <p className="si-note-card-text">{note.text}</p>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>

                                {/* IMAGE GRID */}
                                {imageLoading ? (
                                    <div className="si-loading"><div className="si-spinner" /><p>Loading images...</p></div>
                                ) : sceneImages.length > 0 ? (
                                    <div className="si-masonry">
                                        {sceneImages.map((image) => (
                                            <div key={image.id} className="si-card">
                                                <div className="si-card-img-wrap">
                                                    <img src={image.supabase_storage_url || image.url} alt={image.description || image.filename} className="si-card-img" loading="lazy" />
                                                    {image.source === 'firebase' && (
                                                        <span className="si-card-upload-badge">
                                                            <svg viewBox="0 0 24 24" fill="none" width="10" height="10" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17 8l-5-5-5 5M12 3v12"/></svg>
                                                        </span>
                                                    )}
                                                    <div className="si-card-hover">
                                                        <button className="si-card-remove" onClick={() => removeImageFromList(image.id)}>
                                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14"/>
                                                            </svg>
                                                        </button>
                                                    </div>
                                                </div>
                                                <div className="si-card-meta">
                                                    <div className="si-card-scores">
                                                        <span className="si-badge si-badge-quality">⭐ {image.quality_score?.toFixed(1)}</span>
                                                        {image.emotion_score && <span className="si-badge si-badge-emotion">❤️ {image.emotion_score.toFixed(1)}</span>}
                                                    </div>
                                                    <div className="si-card-tags">
                                                        <span className="si-tag si-tag-scene">{image.scene_type}</span>
                                                        <span className="si-tag si-tag-content">{image.content_type?.replace('_', ' ')}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="si-empty si-empty-sm">
                                        <div className="si-empty-inner">
                                            <div className="si-empty-icon">
                                                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--blue, #6897B7)" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
                                                    <rect x="3" y="3" width="18" height="18" rx="3"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/>
                                                </svg>
                                            </div>
                                            <h3>No images in this scene</h3>
                                            <p>Upload images or save them from the Explore tab.</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                            );
                        })()}
                    </>
                ) : (
                    <div className="si-empty">
                        <div className="si-empty-inner">
                            <div className="si-empty-icon">
                                <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="var(--blue, #6897B7)" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/>
                                </svg>
                            </div>
                            <h3>Create your first board</h3>
                            <p>Organize your images into boards and scenes for different projects.</p>
                            <button className="si-cta-btn" onClick={() => setShowCreateModal(true)}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
                                Create Board
                            </button>
                        </div>
                    </div>
                )}
            </main>

            {/* UPLOAD MODAL */}
            {showUploadModal && (
                <div className="si-modal-overlay">
                    <div className="si-modal si-modal-sm">
                        <h3>{uploadProgress >= 100 ? 'Upload Complete!' : 'Uploading Images'}</h3>
                        <p className="si-modal-sub">{uploadCount.current} of {uploadCount.total} images</p>
                        <div className="si-progress-bar"><div className="si-progress-fill" style={{ width: `${uploadProgress}%` }} /></div>
                        <p className="si-progress-pct">{Math.round(uploadProgress)}%</p>
                    </div>
                </div>
            )}

            {/* CREATE BOARD MODAL */}
            {showCreateModal && (
                <div className="si-modal-overlay">
                    <div className="si-modal">
                        <h3>Create New Board</h3>
                        <input type="text" className="si-modal-input" placeholder="Board name..." value={newListName} onChange={(e) => setNewListName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && createNewList()} autoFocus />
                        <div className="si-modal-actions">
                            <button className="si-btn-ghost" onClick={() => { setShowCreateModal(false); setNewListName(''); }}>Cancel</button>
                            <button className="si-btn-primary" onClick={createNewList} disabled={!newListName.trim()}>Create</button>
                        </div>
                    </div>
                </div>
            )}

            {/* CREATE SCENE MODAL */}
            {showCreateSceneModal && (
                <div className="si-modal-overlay">
                    <div className="si-modal">
                        <h3>Create New Scene</h3>
                        <input type="text" className="si-modal-input" placeholder="Scene name..." value={newSceneName} onChange={(e) => setNewSceneName(e.target.value)} autoFocus />
                        <textarea className="si-modal-textarea" placeholder="Add a description or notes for this scene..." value={newSceneDescription} onChange={(e) => setNewSceneDescription(e.target.value)} rows={4} />
                        <div className="si-modal-actions">
                            <button className="si-btn-ghost" onClick={() => { setShowCreateSceneModal(false); setNewSceneName(''); setNewSceneDescription(''); }}>Cancel</button>
                            <button className="si-btn-primary" onClick={createScene} disabled={!newSceneName.trim()}>Create Scene</button>
                        </div>
                    </div>
                </div>
            )}

            {/* EDIT SCENE MODAL */}
            {editingScene && (
                <div className="si-modal-overlay">
                    <div className="si-modal">
                        <h3>Edit Scene</h3>
                        <input type="text" className="si-modal-input" placeholder="Scene name..." value={editSceneNameValue} onChange={(e) => setEditSceneNameValue(e.target.value)} autoFocus />
                        <textarea className="si-modal-textarea" placeholder="Scene description..." value={editSceneTextValue} onChange={(e) => setEditSceneTextValue(e.target.value)} rows={4} />
                        <div className="si-modal-actions">
                            <button className="si-btn-ghost" onClick={() => setEditingScene(null)}>Cancel</button>
                            <button className="si-btn-primary" onClick={saveSceneEdit} disabled={!editSceneNameValue.trim()}>Save</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StoryIdeas;
